import { useCallback, useState, useEffect } from 'react';
import { useQuery, useMutation, useLazyQuery, useApolloClient } from '@apollo/client';
import { CircularProgress, Container } from '@mui/material';
import dayjs from 'dayjs';
import { Navigate, useLocation, useNavigate } from 'react-router';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useTranslation } from 'react-i18next';

import styles from './ChatMessages.module.css';
import { ConversationHeader } from './ConversationHeader/ConversationHeader';
import { ChatMessage } from './ChatMessage/ChatMessage';
import { ChatInput } from './ChatInput/ChatInput';
import StatusBar from './StatusBar/StatusBar';
import { setNotification, setErrorMessage } from '../../../common/notification';
import {
  SEARCH_QUERY_VARIABLES,
  COLLECTION_SEARCH_QUERY_VARIABLES,
  DEFAULT_MESSAGE_LIMIT,
  DEFAULT_MESSAGE_LOADMORE_LIMIT,
  ISO_DATE_FORMAT,
  GROUP_QUERY_VARIABLES,
  GROUP_COLLECTION_SEARCH_QUERY_VARIABLES,
  getVariables,
} from '../../../common/constants';
import { SEARCH_QUERY } from '../../../graphql/queries/Search';
import { GROUP_SEARCH_QUERY } from 'graphql/queries/WaGroups';
import { SEND_MESSAGE_IN_WA_GROUP, SEND_MESSAGE_IN_WA_GROUP_COLLECTION } from 'graphql/mutations/Group';
import {
  CREATE_AND_SEND_MESSAGE_MUTATION,
  CREATE_AND_SEND_MESSAGE_TO_COLLECTION_MUTATION,
  MARK_AS_READ,
} from '../../../graphql/mutations/Chat';
import { getCachedConverations, updateConversationsCache } from '../../../services/ChatService';
import { addLogs, getDisplayName, isSimulator, updateContactCache } from '../../../common/utils';
import { MessageChannel, messageChannel } from 'common/constants';
import { CollectionInformation } from '../../Collection/CollectionInformation/CollectionInformation';
import { LexicalWrapper } from 'common/LexicalWrapper';
import {
  getCachedGroupConverations,
  updateCacheQuery,
  updateGroupConversationsCache,
} from 'services/GroupMessageService';
import { GET_CONTACT_STATUS } from 'graphql/queries/Contact';
import DotLoader from 'components/UI/DotLoader/DotLoader';

export interface ChatMessagesProps {
  entityId?: number | string | null;
  collectionId?: number | string | null;
  phoneId?: any;
  setPhonenumber?: any;
  appliedFilters?: any;
  channel?: MessageChannel;
}

export const ChatMessages = ({ entityId, collectionId, phoneId, appliedFilters, channel }: ChatMessagesProps) => {
  const urlString = new URL(window.location.href);
  const location = useLocation();
  const client = useApolloClient();
  const navigate = useNavigate();

  const groups: boolean = location.pathname.includes('group');
  const chatType = groups ? 'waGroup' : 'contact';

  let messageParameterOffset: any = 0;
  let searchMessageNumber: any;

  // get the message number from url
  if (urlString.searchParams.get('search')) {
    searchMessageNumber = urlString.searchParams.get('search');
    // check if the message number is greater than 10 otherwise set the initial offset to 0
    messageParameterOffset =
      searchMessageNumber && parseInt(searchMessageNumber, 10) - 10 < 0 ? 1 : parseInt(searchMessageNumber, 10) - 10;
  }

  const [dialog, setDialogbox] = useState<string>();
  const [showDropdown, setShowDropdown] = useState<any>(null);
  const [showLoadMore, setShowLoadMore] = useState(true);
  const [scrolledToMessage, setScrolledToMessage] = useState(false);
  const [scrollToMessageNumber, setScrollToMessageNumber] = useState();
  const [showJumpToLatest, setShowJumpToLatest] = useState(false);
  const [conversationInfo, setConversationInfo] = useState<any>({});
  const [collectionVariables, setCollectionVariables] = useState<any>({});
  const [showNewMessages, setShowNewMessages] = useState<boolean>(false);
  const { t } = useTranslation();
  const hasDateFilter = !!(appliedFilters?.dateFrom || appliedFilters?.dateTo);
  let dialogBox;

  let searchQuery = groups ? GROUP_SEARCH_QUERY : SEARCH_QUERY;

  // get the conversations stored from the cache
  let queryVariables = groups ? GROUP_QUERY_VARIABLES : SEARCH_QUERY_VARIABLES;

  const [getContactStatus] = useLazyQuery(GET_CONTACT_STATUS, {
    variables: { id: entityId },
  });

  useEffect(() => {
    setShowLoadMore(true);
    setScrolledToMessage(false);
    setShowJumpToLatest(false);
  }, [entityId]);

  useEffect(() => {
    setTimeout(() => {
      const messageContainer: any = document.querySelector('.messageContainer');
      if (messageContainer && (appliedFilters ? hasDateFilter : true)) {
        messageContainer.addEventListener('scroll', (event: any) => {
          const messageContainerTarget = event.target;
          if (
            Math.round(messageContainerTarget.scrollTop) ===
            messageContainerTarget.scrollHeight - messageContainerTarget.offsetHeight
          ) {
            setShowJumpToLatest(false);
          } else if (showJumpToLatest === false) {
            setShowJumpToLatest(true);
          }
        });
      }
    }, 1000);
  }, [setShowJumpToLatest, entityId]);

  useEffect(() => {
    if (hasDateFilter) {
      setShowNewMessages(true);
    } else {
      setShowNewMessages(false);
    }
  }, [appliedFilters, entityId]);

  const scrollToLatestMessage = () => {
    const container: any = document.querySelector('.messageContainer');
    if (container) {
      const scroll = container.scrollHeight - container.clientHeight;
      if (scroll) {
        container.scrollTo(0, scroll);
      }
    }
  };

  // Instantiate these to be used later.

  let conversationIndex: number = -1;
  const sendChatMessageMutation = groups ? SEND_MESSAGE_IN_WA_GROUP : CREATE_AND_SEND_MESSAGE_MUTATION;
  const sendCollectionMessageMutation = groups
    ? SEND_MESSAGE_IN_WA_GROUP_COLLECTION
    : CREATE_AND_SEND_MESSAGE_TO_COLLECTION_MUTATION;
  // create message mutation
  const [createAndSendMessage] = useMutation(sendChatMessageMutation);

  const [markAsRead] = useMutation(MARK_AS_READ);

  useEffect(() => {
    if (!groups && entityId) {
      markAsRead({
        variables: { contactId: entityId.toString() },
      }).then(({ data }) => {
        if (data?.markContactMessagesAsRead) {
          updateContactCache(client, entityId);
        }
      });
    }
  }, []);

  if (collectionId) {
    queryVariables = groups ? GROUP_COLLECTION_SEARCH_QUERY_VARIABLES : COLLECTION_SEARCH_QUERY_VARIABLES;
  }

  const {
    loading: conversationLoad,
    error: conversationError,
    data: allConversations,
    networkStatus,
    fetchMore,
  } = useQuery(searchQuery, {
    variables: queryVariables,
    fetchPolicy: 'cache-only',
    notifyOnNetworkStatusChange: true,
  });

  useEffect(() => {
    const clickListener = () => setShowDropdown(null);
    if (showDropdown) {
      window.addEventListener('click', clickListener, true);
    }
    return () => {
      window.removeEventListener('click', clickListener);
    };
  }, [showDropdown]);

  const scrollToMessage = (messageNumberToScroll: any) => {
    setTimeout(() => {
      const scrollElement = document.querySelector(`#search${messageNumberToScroll}`);
      if (scrollElement) {
        scrollElement.scrollIntoView();
      }
    }, 1000);
  };

  // scroll to the particular message after loading
  const getScrollToMessage = () => {
    if (!scrolledToMessage) {
      scrollToMessage(urlString.searchParams.get('search'));
      setScrolledToMessage(true);
    }
  };
  /* istanbul ignore next */
  const handleSearchParameterCompleted = (searchData: any) => {
    if (!searchData || searchData.search.length === 0) return;

    // get the conversations from cache
    const conversations = groups ? getCachedGroupConverations(queryVariables) : getCachedConverations(queryVariables);

    if (!conversations) return;

    const conversationCopy = JSON.parse(JSON.stringify(searchData));
    conversationCopy.search[0].messages
      .sort((currentMessage: any, nextMessage: any) => currentMessage.id - nextMessage.id)
      .reverse();
    const conversationsCopy = JSON.parse(JSON.stringify(conversations));

    conversationsCopy.search = conversationsCopy.search.map((conversation: any) => {
      const conversationObj = conversation;
      if (collectionId) {
        // If the collection(group) is present in the cache
        if (conversationObj.group?.id === collectionId.toString()) {
          conversationObj.messages = conversationCopy.search[0].messages;
        }
        // If the contact is present in the cache
      } else if (conversationObj[chatType]?.id === entityId?.toString()) {
        conversationObj.messages = conversationCopy.search[0].messages;
      }
      return conversationObj;
    });

    // update the conversation cache
    if (groups) {
      updateGroupConversationsCache(conversationsCopy, queryVariables);
    } else {
      updateConversationsCache(conversationsCopy, queryVariables);
    }

    // need to display Load more messages button
    setShowLoadMore(true);
  };

  const [getSearchParameterQuery, { called: parameterCalled, data: parameterdata, loading: parameterLoading }] =
    useLazyQuery<any>(SEARCH_QUERY);

  useEffect(() => {
    // scroll to the particular message after loading
    if (allConversations || parameterdata) getScrollToMessage();
  }, [allConversations, parameterdata]);

  let messageList: any;

  const [sendMessageToCollection] = useMutation(sendCollectionMessageMutation, {
    refetchQueries: [{ query: SEARCH_QUERY, variables: SEARCH_QUERY_VARIABLES }],
  });

  const handleMutationError = (error: any) => {
    const message = error?.message;
    if (message) {
      setNotification(message, 'warning');
    }
  };

  const updatePayload = (payload: any, selectedTemplate: any, variableParam: any) => {
    const payloadCopy = payload;
    // add additional param for template
    if (selectedTemplate) {
      payloadCopy.isHsm = selectedTemplate.isHsm;
      payloadCopy.templateId = parseInt(selectedTemplate.id, 10);
      payloadCopy.params = variableParam;
    }
    return payloadCopy;
  };

  const handleSendMessage = async () => {
    setDialogbox('');
    try {
      await sendMessageToCollection({
        variables: collectionVariables,
      });
      scrollToLatestMessage();
    } catch (error) {
      handleMutationError(error);
    }
  };

  // this function is called when the message is sent collection
  const sendCollectionMessageHandler = async (
    body: string,
    mediaId: string,
    messageType: string,
    selectedTemplate: any,
    variableParam: any
  ) => {
    // display collection info popup

    let payload: any;
    if (groups) {
      payload = {
        mediaId: mediaId,
        message: body,
        type: messageType,
      };
      try {
        await sendMessageToCollection({
          variables: {
            groupId: collectionId,
            input: payload,
          },
        });
        scrollToLatestMessage();
      } catch (error) {
        handleMutationError(error);
      }
    } else {
      setDialogbox('collection');
      payload = {
        body,
        senderId: 1,
        mediaId,
        type: messageType,
        flow: 'OUTBOUND',
      };
      payload = updatePayload(payload, selectedTemplate, variableParam);
    }
    setCollectionVariables({
      groupId: collectionId,
      input: payload,
    });
  };

  // this function is called when the message is sent
  const sendMessageHandler = useCallback(
    async (
      body: any,
      mediaId: string,
      messageType: string,
      selectedTemplate: any,
      variableParam: any,
      interactiveTemplateId: any
    ) => {
      let payload: any;
      if (groups) {
        payload = {
          message: body,
          waManagedPhoneId: conversationInfo?.waGroup?.primaryPhone?.id,
          waGroupId: entityId,
          type: messageType,
          mediaId,
        };
      } else {
        payload = {
          body,
          senderId: 1,
          receiverId: entityId,
          flow: 'OUTBOUND',
          interactiveTemplateId,
          type: messageType,
          mediaId,
          // Decides which transport the server uses. Omitting it would default the message to
          // WhatsApp and send a browser visitor a message they never consented to.
          channel,
        };

        payload = updatePayload(payload, selectedTemplate, variableParam);
      }

      try {
        await createAndSendMessage({
          variables: { input: payload },
        });
        scrollToLatestMessage();
      } catch (error) {
        handleMutationError(error);
      }
    },
    [createAndSendMessage, entityId, phoneId, conversationInfo, channel]
  );

  // loop through the cached conversations and find if contact/Collection exists
  const updateConversationInfo = (type: string, Id: any) => {
    allConversations.search.map((conversation: any, index: any) => {
      if (conversation[type].id === Id.toString()) {
        conversationIndex = index;
        setConversationInfo(conversation);
      }
      return null;
    });
  };

  const findContactInAllConversations = () => {
    if (allConversations && allConversations.search) {
      // loop through the cached conversations and find if contact exists
      // need to check - updateConversationInfo('contact', entityId);
      allConversations.search.map((conversation: any, index: any) => {
        if (conversation[chatType]?.id === entityId?.toString()) {
          conversationIndex = index;
          setConversationInfo(conversation);
        }
        return null;
      });
    }

    // if conversation is not present then fetch for contact
    if (conversationIndex < 0) {
      if (!conversationLoad || (allConversations && allConversations.search[0][chatType].id !== entityId)) {
        const variables = getVariables(
          { limit: 1 },
          {
            limit: DEFAULT_MESSAGE_LIMIT,
            offset: messageParameterOffset,
          },
          { filter: { id: entityId } },
          groups
        );

        addLogs(`if conversation is not present then search for contact-${entityId}`, variables);

        fetchMore({
          variables,
          updateQuery: (prev: any, { fetchMoreResult }: any) => {
            if (fetchMoreResult.search && fetchMoreResult.search.length) {
              return updateCacheQuery(prev, fetchMoreResult, entityId, collectionId, chatType);
            } else {
              getContactStatus().then(({ data }) => {
                if (data && data.contact && data.contact.contact && data.contact.contact.status) {
                  const status = data.contact.contact.status;
                  if (status === 'BLOCKED') {
                    setNotification('The contact is blocked', 'warning');
                    navigate('/chat');
                  }
                }
              });
            }
          },
        });
      }
      // lets not get from cache if parameter is present
    } else if (conversationIndex > -1 && messageParameterOffset) {
      if (
        (!parameterLoading && !parameterCalled) ||
        (parameterdata && parameterdata.search[0][chatType].id !== entityId)
      ) {
        const variables = getVariables(
          { limit: 1 },
          { limit: DEFAULT_MESSAGE_LIMIT, offset: messageParameterOffset },
          { filter: { id: entityId } },
          groups
        );

        addLogs(`if search message is not present then search for contact-${entityId}`, variables);

        getSearchParameterQuery({
          variables,
        }).then(({ data }) => handleSearchParameterCompleted(data));
      }
    }
  };

  const findCollectionInAllConversations = () => {
    // loop through the cached conversations and find if collection exists
    if (allConversations && allConversations.search) {
      allConversations.search.map((conversation: any, index: any) => {
        if (conversation?.group?.id === collectionId?.toString()) {
          conversationIndex = index;
          setConversationInfo(conversation);
        }
        return null;
      });

      // if conversation is not present then fetch the collection
      if (conversationIndex < 0) {
        if (!conversationLoad || allConversations.search[0]?.group?.id !== collectionId) {
          const variables = getVariables(
            { limit: 1 },
            {
              limit: DEFAULT_MESSAGE_LIMIT,
              offset: messageParameterOffset,
            },
            { filter: { id: collectionId, searchGroup: true } },
            groups
          );
          addLogs(`if conversation is not present then search for contact-${entityId}`, variables);

          fetchMore({
            variables,
            updateQuery: (prev: any, { fetchMoreResult }: any) =>
              updateCacheQuery(prev, fetchMoreResult, entityId, collectionId, chatType),
          });
        }
        // lets not get from cache if parameter is present
      } else {
        updateConversationInfo('group', collectionId);
      }
    }
  };

  // find if contact/Collection present in the cached
  useEffect(() => {
    if (entityId) {
      findContactInAllConversations();
    } else if (collectionId) {
      findCollectionInAllConversations();
    }
  }, [entityId, collectionId, allConversations]);

  useEffect(() => {
    if (searchMessageNumber) {
      const element = document.querySelector(`#search${searchMessageNumber}`);
      if (element) {
        element.scrollIntoView();
      } else {
        // need to check if message is not present fetch message from selected contact
      }
    }
  }, [searchMessageNumber]);

  // HOOKS ESTABLISHED ABOVE

  // Run through these cases to ensure data always exists
  if (conversationError) {
    setErrorMessage(conversationError);
    return null;
  }

  // check if the search API results nothing for a particular contact ID and redirect to chat
  if (entityId && allConversations) {
    if (allConversations.search.length === 0 || allConversations.search[0][chatType]?.status === 'BLOCKED') {
      return <Navigate to="/chat" />;
    }
  }

  const showEditDialog = (id: number) => {
    setShowDropdown(id);
  };

  // on reply message scroll to replied message or fetch if not present
  const jumpToMessage = (messageNumber: number) => {
    const element = document.querySelector(`#search${messageNumber}`);
    if (element) {
      element.scrollIntoView();
    } else {
      const offset = messageNumber - 10 <= 0 ? 1 : messageNumber - 10;
      const variables: any = getVariables(
        { limit: 1 },
        {
          limit: conversationInfo.messages[0].messageNumber,
          offset,
        },
        { filter: { id: entityId?.toString() } },
        groups
      );

      addLogs(`fetch reply message`, variables);

      fetchMore({
        variables,
      });

      scrollToMessage(messageNumber);
    }
  };

  const showDaySeparator = (currentDate: string, nextDate: string) => {
    // if it's last message and its date is greater than current date then show day separator
    if (!nextDate && dayjs(currentDate).format(ISO_DATE_FORMAT) < dayjs().format(ISO_DATE_FORMAT)) {
      return true;
    }

    // if the day is changed then show day separator
    if (nextDate && dayjs(currentDate).format(ISO_DATE_FORMAT) > dayjs(nextDate).format(ISO_DATE_FORMAT)) {
      return true;
    }

    return false;
  };

  if (conversationInfo && conversationInfo.messages && conversationInfo.messages?.length > 0) {
    // A contact is one person across both channels, but a thread is not: replying in the
    // WhatsApp view to something they said in the browser would put the answer on the wrong
    // channel. Filtered at render, not in the query, so the thread still updates live.
    let reverseConversation = channel
      ? conversationInfo.messages.filter((message: any) => messageChannel(message) === channel)
      : [...conversationInfo.messages];

    reverseConversation = reverseConversation.map((message: any, index: number) => {
      return (
        <ChatMessage
          groups={groups}
          {...message}
          entityId={entityId}
          key={message.messageNumber}
          popup={message.id === showDropdown}
          onClick={() => showEditDialog(message.id)}
          focus={index === 0}
          jumpToMessage={jumpToMessage}
          showIcon={!groups}
          daySeparator={showDaySeparator(
            reverseConversation[index].insertedAt,
            reverseConversation[index + 1] ? reverseConversation[index + 1].insertedAt : null
          )}
        />
      );
    });

    messageList = reverseConversation
      .sort((currentMessage: any, nextMessage: any) => currentMessage.id - nextMessage.id)
      .reverse();
  }

  const canLoadMore = (direction = 'past') => {
    if (!conversationInfo?.messages?.length) return false;

    if (hasDateFilter) {
      const messages = conversationInfo.messages;
      const targetMessage = direction === 'past' ? messages[messages.length - 1] : messages[0];

      if (direction === 'past') {
        return targetMessage.messageNumber > 1;
      } else {
        return true;
      }
    }
    return showLoadMore;
  };

  const loadMoreMessages = (direction?: 'past' | 'future') => {
    if (hasDateFilter && direction) {
      loadMoreMessagesForDateRange(direction);
    } else {
      loadMoreMessagesRegular();
    }
  };

  const loadMoreMessagesForDateRange = (direction: 'past' | 'future' = 'past') => {
    const messages = conversationInfo.messages;
    const targetMessage = direction === 'past' ? messages[messages.length - 1] : messages[0];

    const { messageNumber } = targetMessage;

    const variables: any = getVariables(
      { limit: 1 },
      {
        limit: DEFAULT_MESSAGE_LIMIT,
        offset: direction === 'past' ? Math.max(1, messageNumber - DEFAULT_MESSAGE_LIMIT) : messageNumber + 1,
      },
      { filter: { id: entityId?.toString() } },

      groups
    );

    fetchMore({
      variables,
      updateQuery: (prev: any, { fetchMoreResult }: any) =>
        updateCacheQuery(prev, fetchMoreResult, entityId, collectionId, chatType, true, direction),
    }).then((fetchMoreResult: any) => {
      const conversationData = fetchMoreResult.data;

      if (
        (conversationData && conversationData.search.length === 0) ||
        conversationData.search[0].messages.length === 0
      ) {
        if (direction === 'past') {
          setShowLoadMore(false);
        } else {
          setShowNewMessages(false);
        }
      }

      setScrollToMessageNumber(messageNumber);
    });
  };

  const loadMoreMessagesRegular = () => {
    const { messageNumber } = conversationInfo.messages[conversationInfo.messages.length - 1];

    const variables: any = getVariables(
      { limit: 1 },
      {
        limit: messageNumber > DEFAULT_MESSAGE_LOADMORE_LIMIT ? DEFAULT_MESSAGE_LOADMORE_LIMIT - 1 : messageNumber - 2,
        offset:
          messageNumber - DEFAULT_MESSAGE_LOADMORE_LIMIT <= 0 ? 1 : messageNumber - DEFAULT_MESSAGE_LOADMORE_LIMIT,
      },
      { filter: { id: entityId?.toString() } },
      groups
    );

    if (collectionId) {
      variables.filter = { id: collectionId.toString(), searchGroup: true };
    }

    executeLoadMore(variables, messageNumber);
  };

  const executeLoadMore = (variables: any, messageNumber: any, direction: any = 'past') => {
    addLogs(`load More Messages-${collectionId}`, variables);

    fetchMore({
      variables,
      updateQuery: (prev: any, { fetchMoreResult }: any) =>
        updateCacheQuery(prev, fetchMoreResult, entityId, collectionId, chatType, true, direction),
    }).then((fetchMoreResult: any) => {
      const conversationData = fetchMoreResult.data;

      if (
        (conversationData && conversationData.search.length === 0) ||
        conversationData.search[0].messages.length === 0
      ) {
        if (direction === 'past') {
          setShowLoadMore(false);
        } else {
          setShowNewMessages(false);
        }
      }

      setScrollToMessageNumber(messageNumber);
    });
  };

  useEffect(() => {
    const element = document.querySelector(`#search${scrollToMessageNumber}`);
    if (element) {
      element.scrollIntoView();
    }
  }, [scrollToMessageNumber]);

  let chatMessages;
  // Check if there are conversation messages else display no messages
  if (messageList) {
    const loadMoreOption =
      conversationInfo.messages?.length > DEFAULT_MESSAGE_LIMIT - 1 ||
      (searchMessageNumber && searchMessageNumber > 19);
    chatMessages = (
      <>
        {canLoadMore() && loadMoreOption && (
          <div className={styles.LoadMore}>
            {conversationLoad ? (
              <CircularProgress data-testid="loading" className={styles.Loading} />
            ) : (
              <div
                className={styles.LoadMoreButton}
                onClick={() => loadMoreMessages('past')}
                onKeyDown={() => loadMoreMessages('past')}
                aria-hidden="true"
                data-testid="loadMoreMessages"
              >
                {t('Load more messages')}
              </div>
            )}
          </div>
        )}

        {messageList}
      </>
    );
  } else {
    chatMessages = (
      <div className={styles.NoMessages} data-testid="messageContainer">
        {t('No messages.')}
      </div>
    );
  }

  const messageListContainr = (
    <Container
      className={`${styles.MessageList} messageContainer `}
      style={{ height: `calc(100% - 215px)` }}
      maxWidth={false}
      data-testid="messageContainer"
    >
      <StatusBar />
      {chatMessages}
    </Container>
  );

  const handleChatClearedAction = () => {
    const conversationInfoCopy = JSON.parse(JSON.stringify(conversationInfo));
    conversationInfoCopy.messages = [];
    let allConversationsCopy: any = [];
    allConversationsCopy = JSON.parse(JSON.stringify(allConversations));
    const index = conversationIndex === -1 ? 0 : conversationIndex;
    allConversationsCopy.search[index] = conversationInfoCopy;
    // update allConversations in the cache
    if (groups) {
      updateGroupConversationsCache(allConversationsCopy, queryVariables);
    } else {
      updateConversationsCache(allConversationsCopy, queryVariables);
    }
  };

  // conversationInfo should not be empty
  if (!groups && !Object.prototype.hasOwnProperty.call(conversationInfo, 'contact')) {
    return (
      <div className={styles.LoadMore}>
        <CircularProgress data-testid="loading" className={styles.Loading} />
      </div>
    );
  }

  const showLatestMessage = () => {
    setShowJumpToLatest(false);

    // check if we have offset 0 (messageNumber === offset)
    if (conversationInfo.messages[0].messageNumber !== 0) {
      // set limit to default message limit
      const limit = DEFAULT_MESSAGE_LIMIT;

      // set variable for contact chats
      const variables: any = getVariables(
        { limit: 1 },
        { limit, offset: 0 },
        { filter: { id: entityId?.toString() } },
        groups
      );

      // if collection, replace id with collection id
      if (collectionId) {
        variables.filter = { id: collectionId.toString(), searchGroup: true };
      }

      addLogs(`show Latest Message for contact-${entityId}`, variables);

      getSearchParameterQuery({
        variables,
      }).then(({ data }) => handleSearchParameterCompleted(data));
    }

    scrollToLatestMessage();
  };

  const jumpToLatest = (
    <div
      data-testid="jumpToLatest"
      className={styles.JumpToLatest}
      onClick={() => showLatestMessage()}
      onKeyDown={() => showLatestMessage()}
      aria-hidden="true"
    >
      {t('Jump to latest')}
      <ExpandMoreIcon />
    </div>
  );

  let topChatBar;
  let chatInputSection;

  const isSimulatorProp = groups ? false : isSimulator(conversationInfo.contact?.phone);
  const showCurrentMessages = () => {
    loadMoreMessagesForDateRange('future');
  };

  const showCurrentMesssage = (
    <div data-testid="show-current-messages" onClick={showCurrentMessages} className={styles.ShowNewMessages}>
      {networkStatus === 3 ? (
        <DotLoader />
      ) : (
        <>
          Load more
          <ExpandMoreIcon />
        </>
      )}
    </div>
  );

  if (entityId && conversationInfo[chatType]) {
    const displayName = groups ? conversationInfo.waGroup.label : getDisplayName(conversationInfo[chatType]);

    topChatBar = (
      <ConversationHeader
        displayName={displayName}
        isSimulator={isSimulatorProp}
        entityId={entityId.toString()}
        contact={{
          lastMessageTime: conversationInfo[chatType]?.lastMessageAt,
          contactStatus: conversationInfo[chatType]?.status,
          contactBspStatus: conversationInfo[chatType]?.bspStatus,
        }}
        handleAction={() => handleChatClearedAction()}
        groups={groups}
        channel={channel}
      />
    );

    chatInputSection = (
      <div className={styles.ChatInput}>
        {conversationInfo.messages.length && !showNewMessages && showJumpToLatest ? jumpToLatest : null}
        <LexicalWrapper>
          <ChatInput
            onSendMessage={sendMessageHandler}
            lastMessageTime={conversationInfo[chatType]?.lastMessageAt}
            contactStatus={conversationInfo[chatType]?.status}
            contactBspStatus={conversationInfo[chatType]?.bspStatus}
            showAttachmentButton={!groups}
            channel={channel}
          />
        </LexicalWrapper>
      </div>
    );
  } else if (collectionId && conversationInfo.group) {
    topChatBar = (
      <ConversationHeader
        collectionId={collectionId.toString()}
        displayName={conversationInfo.group.label}
        handleAction={handleChatClearedAction}
        groups={groups}
      />
    );

    chatInputSection = (
      <div className={styles.ChatInput}>
        {conversationInfo.messages && conversationInfo.messages.length && showJumpToLatest ? jumpToLatest : null}
        <LexicalWrapper>
          <ChatInput onSendMessage={sendCollectionMessageHandler} isCollection showAttachmentButton={!groups} />
        </LexicalWrapper>
      </div>
    );
  }

  return (
    <Container data-testid="message-container" className={styles.ChatMessages} maxWidth={false} disableGutters>
      {dialogBox}
      {dialog === 'collection' ? (
        <CollectionInformation
          collectionId={collectionId}
          displayPopup
          setDisplayPopup={() => setDialogbox('')}
          handleSendMessage={() => handleSendMessage()}
        />
      ) : null}
      {topChatBar}
      {messageListContainr}
      {chatInputSection}
      {showNewMessages && showCurrentMesssage}
    </Container>
  );
};

export default ChatMessages;
